import SelectComponent from "@via/components/select/select";
import { useProcessMenuGroupsList } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { isSelectOption, SelectOption } from "@via/utils/interfaces.tsx";
import { ProcessMenuGroupType } from "common";
import { JSX, useMemo } from "react";

/** One revision the user is about to consent to: the fields a scan row carries for it. */
export type ConsentItem = {
  repoPath: string;
  revId: number;
  commitId: string;
  name: string | null;
};

/**
 * Consent, which is the point of this wizard rather than decoration on top of it.
 *
 * An approved security spike requires the user be shown the source repository and told the code
 * is untrusted before anything is stored. For a batch that means one checkbox over a list: the
 * text names the repository, the number of processes, and every commit id in full, because the
 * batch binds each item to exactly the commit shown here (the server skips an item whose tag has
 * moved since). The list is the same array the modal sends as the batch's items, so what is named
 * here is what is imported. The untrusted code wording follows the single import's server
 * composed consent sentence; the scan response carries no consent text, so it is composed here
 * from values the scan reported verbatim.
 *
 * The sentence saying each process is checked again at import time is required, not decorative:
 * divergence, the uuid race and the classifier re-check all run at apply time, so this wizard
 * cannot promise an all or nothing outcome, and consent text implying one would be false.
 *
 * The menu group lives here because the batch requires one and this is the last step before the
 * write. The picker reads the group's id off `useProcessMenuGroupsList`, not its name: the `.vf`
 * import wizard round trips menu groups by name for its own historical reasons (a bundle that
 * predates group ids), and that round trip is not copied here.
 *
 * The checkbox starts unchecked and Import stays disabled until it is ticked and a menu group is
 * chosen. Nothing defaults it to checked, and nothing about ticking it is implied by any earlier
 * step: this is the fail closed gate the rest of the wizard exists to reach.
 *
 * `importLocked` keeps Import disabled whatever the checkbox says, and offers "Scan again"
 * instead. The modal sets it when a batch got no response: the server may still be importing
 * that list, so the only honest next step is a new scan, which shows what is already here.
 */
export default function ConsentStep({
  repoUrl,
  items,
  menuGroupId,
  onMenuGroupIdChange,
  consentChecked,
  onConsentCheckedChange,
  onImport,
  isImporting,
  importLocked = false,
  onScanAgain,
  errorMessage,
}: {
  repoUrl: string;
  items: ConsentItem[];
  menuGroupId: number | null;
  onMenuGroupIdChange: (id: number) => void;
  consentChecked: boolean;
  onConsentCheckedChange: (checked: boolean) => void;
  onImport: () => void;
  isImporting: boolean;
  importLocked?: boolean;
  onScanAgain?: () => void;
  errorMessage: string | null;
}): JSX.Element {
  const { data: menuGroupList, isLoading: isMenuGroupListLoading } =
    useProcessMenuGroupsList(String(ProcessMenuGroupType.Process));

  const menuGroupOptions: SelectOption[] = useMemo(
    () =>
      (menuGroupList?.data ?? []).map(
        (group: { id: number; name: string }) => ({
          label: group.name,
          value: group.id,
        })
      ),
    [menuGroupList]
  );

  const selectedMenuGroup =
    menuGroupOptions.find(option => option.value === menuGroupId) ?? null;

  const count = items.length;
  const canImport =
    consentChecked &&
    null !== menuGroupId &&
    count > 0 &&
    !isImporting &&
    !importLocked;

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1 text-sm font-medium text-gray-900">Menu group</p>
        <SelectComponent
          name="menuGroupId"
          options={menuGroupOptions}
          isLoading={isMenuGroupListLoading}
          value={selectedMenuGroup}
          enableClear={false}
          placeholder="Select a menu group"
          onChange={selected => {
            // `isSelectOption` is the narrowing `Array.isArray` cannot do here: `Options<T>` is a
            // readonly array, not `T[]`, so `Array.isArray` never proves the negative case and a
            // cast on top of it would be unchecked rather than narrowed.
            if (isSelectOption(selected)) {
              onMenuGroupIdChange(selected.value as number);
            }
          }}
        />
        <p className="mt-1 text-xs text-gray-500">
          Every process imported in this batch goes under this menu group.
        </p>
      </div>

      <div
        className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900"
        data-testid="importConsentText">
        <p>
          You are about to import untrusted code for {count}{" "}
          {1 === count ? "process" : "processes"} from{" "}
          <span className="break-all font-mono">{repoUrl}</span>, at the commits
          listed below. Foundry will store it but will not execute it. Review
          the code before using it.
        </p>
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={`${item.repoPath}:${item.revId}:${idx}`}>
              <p className="font-medium">
                {item.name ?? item.repoPath}{" "}
                <span className="font-normal text-gray-500">
                  ({item.repoPath}, revision {item.revId})
                </span>
              </p>
              <code className="block break-all font-mono text-xs text-gray-700">
                {item.commitId}
              </code>
            </li>
          ))}
        </ul>
        <p className="font-medium">
          Each process is checked again at import time, and any that fails is
          reported rather than imported.
        </p>
      </div>

      <label className="flex items-start gap-2 text-sm text-gray-900">
        <input
          type="checkbox"
          className="mt-0.5"
          data-testid="importProcessConsent"
          checked={consentChecked}
          onChange={event => onConsentCheckedChange(event.target.checked)}
        />
        <span>
          I have read the source and understand this code will be stored but not
          executed.
        </span>
      </label>
      {/* The modal blocks every close path while the batch runs, because a batch that finished
          after a close would lose every per item result. This says so, so a blocked close is
          explained rather than a silent no-op. */}
      {isImporting ? (
        <p
          className="text-sm text-gray-700"
          data-testid="importInProgressNotice">
          Importing {count} {1 === count ? "process" : "processes"}. This window
          stays open until the import finishes, so you can see what happened to
          each one.
        </p>
      ) : null}
      {null !== errorMessage ? (
        <p className="text-sm text-red-600" data-testid="importConsentError">
          {errorMessage}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        {importLocked && undefined !== onScanAgain ? (
          <button
            type="button"
            data-testid="importScanAgain"
            onClick={onScanAgain}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Scan again
          </button>
        ) : null}
        <button
          type="button"
          data-testid="importProcessApprove"
          disabled={!canImport}
          onClick={onImport}
          className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed">
          {isImporting ? "Importing..." : "Import"}
        </button>
      </div>
    </div>
  );
}
